import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SEOHead from '../components/seo/SEOHead';
import useScrollToTop from '../hooks/useScrollToTop';
import Header from '../components/core/Header';
import Footer from '../components/core/Footer';
import { clearConsent, getConsent } from '../utils/cookieConsent';

const Privacy = () => {
  useScrollToTop();
  const [showResetMessage, setShowResetMessage] = useState(false);
  const currentConsent = getConsent();

  const handleResetCookies = () => {
    clearConsent();
    setShowResetMessage(true);
    setTimeout(() => {
      window.location.reload(); // Reload to show banner again
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title="Privacy Policy - CustomSync"
        description="CustomSync privacy policy - Learn how we collect, use, and protect your data."
        keywords="privacy policy, data protection, CustomSync privacy"
        type="article"
        url="https://customsync.app/privacy"
      />
      
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">CustomSync Privacy Policy</h1>
          <p className="text-gray-600 mb-8">Effective Date: October 7, 2025</p>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 mb-8">
              CustomSync ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. 
              This Privacy Policy explains how we collect, use, store, and protect information when you use our website and services.
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Overview</h2>
              <p className="text-gray-700">
                CustomSync is a web-based integration tool that allows Amazon Sellers to automatically import Amazon Custom order data 
                (including customization text and images) into supported shipping and order management platforms such as ShipStation and Veeqo.
              </p>
              <p className="text-gray-700 mt-4">
                We collect and process data only as necessary to perform these integrations on behalf of our users.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
              <p className="text-gray-700 mb-4">
                We may collect the following types of information:
              </p>

              <div className="ml-4 space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">a. Account Information</h3>
                  <p className="text-gray-700">
                    When you sign up or log in, we collect your name, email address, and login credentials (via secure OAuth tokens).
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">b. Amazon Order Data</h3>
                  <p className="text-gray-700 mb-2">
                    When authorized by you, we access Amazon order information through the Amazon Selling Partner API (SP-API), including:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                    <li>Order identifiers</li>
                    <li>Customization data (text, image URLs)</li>
                    <li>Shipment details (e.g., shipping service, item name, SKU)</li>
                  </ul>
                  <p className="text-gray-700 mt-2">
                    We do not access or store sensitive buyer information (e.g., addresses, phone numbers, or payment data).
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">c. Usage Data</h3>
                  <p className="text-gray-700 mb-2">
                    We collect limited technical information for debugging and analytics, such as:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                    <li>Request timestamps</li>
                    <li>API success/error rates</li>
                    <li>Browser and system information (for app performance)</li>
                  </ul>
                  <p className="text-gray-700 mt-2">
                    No PII is stored in logs.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Information</h2>
              <p className="text-gray-700 mb-3">
                We use collected information to:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Import and synchronize customization data between Amazon and third-party apps</li>
                <li>Maintain service performance and reliability</li>
                <li>Improve user experience and fix technical issues</li>
                <li>Comply with legal and regulatory requirements</li>
              </ul>
              <p className="text-gray-700 mt-4">
                <strong>We never sell, rent, or share data with any unauthorized third party.</strong>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Storage & Retention</h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Order data is temporarily cached during synchronization and deleted automatically within 30 days of import.</li>
                <li>Persistent data (such as user settings) is stored in Google Cloud Firestore, encrypted at rest using AES-256.</li>
                <li>Backups are encrypted and retained for 30 days, then purged automatically.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Security</h2>
              <p className="text-gray-700 mb-3">
                We employ industry-standard security practices, including:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>TLS 1.2+ encryption for all data in transit</li>
                <li>AES-256 encryption for data at rest</li>
                <li>Google Cloud-managed IAM with MFA for all developer accounts</li>
                <li>Access control and logging for all administrative actions</li>
              </ul>
              <p className="text-gray-700 mt-4">
                We review permissions quarterly and follow Amazon's Data Protection Policy guidelines.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Sharing</h2>
              <p className="text-gray-700 mb-3">
                We share order data only with third-party platforms that you explicitly authorize, such as:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                <li>ShipStation</li>
                <li>Veeqo</li>
              </ul>
              <p className="text-gray-700 mt-4">
                Each integration occurs through secure API connections using encrypted credentials. We also work with trusted data partners 
                for website visitor identification as described in Section 7 below.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Cookies, Similar Technologies & Data Partners</h2>
              <p className="text-gray-700 mb-4">
                When you visit or log in to our website, cookies and similar technologies may be used by our online data partners or vendors to associate these activities with other personal information they or others have about you, including by association with your email. We (or service providers on our behalf) may then send communications and marketing to these email addresses. You may opt out of receiving this advertising by visiting{' '}
                <a href="https://app.retention.com/optout" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                  https://app.retention.com/optout
                </a>.
              </p>
              <p className="text-gray-700 mb-4">
                You also have the option to opt out of the collection of your personal data in compliance with GDPR. To exercise this option, please visit{' '}
                <a href="https://www.rb2b.com/rb2b-gdpr-opt-out" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                  https://www.rb2b.com/rb2b-gdpr-opt-out
                </a>.
              </p>
              <p className="text-gray-600 text-sm">
                For more on how we use cookies and how to manage your preferences, see Section 13 below (Cookie Preferences).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Data Disposal</h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Users can request deletion of all associated data at any time by contacting{' '}
                  <a href="mailto:hello@customsync.app" className="text-blue-600 hover:text-blue-800 underline">
                    hello@customsync.app
                  </a>.
                </li>
                <li>All order data and related files are automatically deleted within 30 days of import.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Access Control</h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Access to systems handling Amazon data is restricted to authorized personnel only.</li>
                <li>All internal accounts are protected by strong passwords and Multi-Factor Authentication (MFA).</li>
                <li>Credentials are stored securely in Google Secret Manager.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Your Rights</h2>
              <p className="text-gray-700 mb-3">
                You have the right to:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Request access to the data we hold about you</li>
                <li>Request correction or deletion of your data</li>
                <li>Withdraw consent for data processing at any time</li>
              </ul>
              <p className="text-gray-700 mt-4">
                To exercise these rights, please email{' '}
                <a href="mailto:hello@customsync.app" className="text-blue-600 hover:text-blue-800 underline">
                  hello@customsync.app
                </a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to This Policy</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy as our practices evolve or to comply with new laws.
                The latest version will always be available at{' '}
                <a href="https://customsync.app/privacy" className="text-blue-600 hover:text-blue-800 underline">
                  https://customsync.app/privacy
                </a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Contact Us</h2>
              <p className="text-gray-700 mb-3">
                If you have questions about this Privacy Policy or our data handling practices, contact:
              </p>
              <div className="bg-gray-50 rounded-lg">
                <p className="text-gray-900 font-semibold mb-2">CustomSync</p>
                <p className="text-gray-700">
                  Email:{' '}
                  <a href="mailto:hello@customsync.app" className="text-blue-600 hover:text-blue-800 underline">
                    hello@customsync.app
                  </a>
                  <br />
                  Website:{' '}
                  <a href="https://customsync.app" className="text-blue-600 hover:text-blue-800 underline" target="_blank" rel="noopener noreferrer">
                    https://customsync.app
                  </a>
                </p>
              </div>
            </section>

            <section id="cookies" className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Cookie Preferences</h2>
              <p className="text-gray-700 mb-4">
                You can manage your cookie preferences at any time. We respect your privacy choices.
              </p>
              
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Current Cookie Settings</h3>
                    <p className="text-sm text-gray-600">
                      Your current consent level: <span className="font-medium text-blue-600">{currentConsent || 'Not set'}</span>
                    </p>
                  </div>
                </div>

                {showResetMessage && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-800 text-sm">
                    ✓ Cookie preferences reset. Reloading page...
                  </div>
                )}

                <button
                  onClick={handleResetCookies}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
                >
                  Change Cookie Preferences
                </button>
                
                <p className="text-xs text-gray-500 mt-3">
                  This will reset your cookie preferences and show the consent banner again.
                </p>
              </div>

              <div className="mt-6 space-y-4">
                <div className="bg-gray-50 p-2 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">🍪 Essential Cookies</h4>
                  <p className="text-sm text-gray-600">
                    These cookies are necessary for the website to function and cannot be disabled. 
                    They include cookies that remember your cookie preferences.
                  </p>
                </div>

                <div className="bg-gray-50 p-2 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">📊 Analytics Cookies</h4>
                  <p className="text-sm text-gray-600">
                    We use Firebase Analytics to understand how visitors interact with our website. 
                    This helps us improve the user experience. These cookies can be disabled.
                  </p>
                </div>

                <div className="bg-gray-50 p-2 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">🏢 B2B Visitor Identification</h4>
                  <p className="text-sm text-gray-600">
                    We use cookies and similar technologies from our data partners (e.g. RB2B) to identify visiting companies and improve our outreach. 
                    You may opt out via the links in Section 7 above.
                  </p>
                </div>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Privacy;

